import { formatCurrency, formatDate } from 'utils/utils';

/**
 * Flattens the `context` object the drafting endpoint returns alongside a draft
 * into a display list of the Salesforce records that grounded the generated
 * email, so the user can see we only used data from their own org.
 *
 * The endpoint fetches a different set of context parts per topic (see
 * TOPIC_CONTEXT in alps-be's email_drafts_controller), so every part here is
 * optional and absent parts simply contribute no rows.
 */

const joinDetails = (parts) => parts.filter(Boolean).join(' · ');

const mapFundraising = (fundraising) =>
	fundraising && {
		id: fundraising.id,
		label: 'Fundraising',
		primaryText: fundraising.name || fundraising.entity?.name || 'Fundraising',
		secondaryText: joinDetails([
			fundraising.entity?.name,
			fundraising.closing_round,
			fundraising.anticipated_close_date
				? `Closes ${formatDate(fundraising.anticipated_close_date, true)}`
				: null,
		]),
	};

// current_investment and investor_summary items are Investor records
// (AIM__Investor__c) — the account's commitment to an entity — not
// AIM__Investment__c rows.
const mapCurrentCommitment = (investor) =>
	investor && {
		id: investor.fund?.id,
		label: 'Current investment',
		primaryText: investor.fund?.name || 'Investment',
		secondaryText: joinDetails([
			investor.commitment ? formatCurrency(investor.commitment) : null,
			investor.commitment_date
				? formatDate(investor.commitment_date, true)
				: null,
		]),
	};

const mapInvestorSummary = (investor) => ({
	id: investor.fund?.id,
	label: 'Fund commitment',
	primaryText: investor.fund?.name || 'Investment',
	secondaryText: joinDetails([
		investor.commitment ? formatCurrency(investor.commitment) : null,
		investor.fund?.status,
	]),
});

const mapRaisingEntity = (entity) => ({
	id: entity.id,
	label: 'Fund raising',
	primaryText: entity.name || 'Fund',
	secondaryText: joinDetails([
		entity.strategy,
		entity.target_size ? `Target ${formatCurrency(entity.target_size)}` : null,
	]),
});

// `investment` is AIM__Investment1__c, a monetary amount rather than a name, so
// the portfolio company (or failing that, the fund) is the row's title.
const mapInvestmentNews = (news) => {
	const primaryText = news.account_name || news.entity?.name;

	return {
		id: news.id,
		label: news.exited ? 'Portfolio exit' : 'Portfolio investment',
		primaryText,
		secondaryText: joinDetails([
			news.entity?.name === primaryText ? null : news.entity?.name,
			news.investment ? formatCurrency(news.investment) : null,
			news.type,
			news.close_date ? formatDate(news.close_date, true) : null,
		]),
	};
};

const mapInteraction = (interaction) => ({
	id: interaction.id,
	label: 'Interaction',
	primaryText: interaction.subject || interaction.name || 'Interaction',
	secondaryText: joinDetails([
		interaction.type,
		interaction.date ? formatDate(interaction.date, true) : null,
	]),
});

/**
 * @param {Object} context - The `context` object from the generate-draft response
 * @returns {Array<{id: string, label: string, primaryText: string, secondaryText: string}>}
 */
export const buildDraftOutreachSources = (context) => {
	if (!context) return [];

	const account = context.account;

	const rows = [
		mapFundraising(context.fundraising),
		mapCurrentCommitment(context.current_investment),
		...(context.investor_summary || []).map(mapInvestorSummary),
		...(context.raising_funds || []).map(mapRaisingEntity),
		...(account?.recent_interactions || []).map(mapInteraction),
		account?.last_interaction ? mapInteraction(account.last_interaction) : null,
		...(context.investment_news || []).map(mapInvestmentNews),
	].filter((row) => row?.primaryText);

	// The same record can be reached through more than one context part (an
	// entity is both the current commitment and a row in the investor summary; the last
	// interaction is usually the newest recent one), and showing it twice reads
	// as if the draft was grounded in more data than it was.
	const seen = new Set();

	return rows.filter((row) => {
		if (!row.id) return true;
		if (seen.has(row.id)) return false;

		seen.add(row.id);

		return true;
	});
};

export default buildDraftOutreachSources;
